import { useState, useEffect } from 'react';
import { useToast } from '../components/ui/toast';
import { usersAPI, rolesAPI, UpdateUserRequest } from '../services/api';
import { useAuthStore } from '../stores';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  ChevronLeft,
  ChevronRight,
  Trash2,
  Edit,
  MoreHorizontal,
  Users as UsersIcon,
  Loader2
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';

interface Role {
  id: string;
  name: string;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  role_id?: string;
  role?: Role;
  created_at: string;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirmation: '',
    is_active: true,
    role_id: '',
  });

  const { addToast } = useToast();

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [roleFilter]);

  const fetchUsers = async () => {
    try {
      const params = roleFilter ? { role_id: roleFilter } : undefined;
      const response = await usersAPI.list(params);
      setUsers(response.data.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      addToast({
        title: "Error",
        description: "Failed to load users. Please refresh the page.",
        variant: "destructive"
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await rolesAPI.list();
      setRoles(response.data.data.roles || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim() || !formData.password.trim()) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      addToast({
        title: "Validation Error",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    setCreateLoading(true);

    try {
      const response = await usersAPI.create({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        is_active: formData.is_active,
        role_id: formData.role_id || undefined,
      });
      
      const newUser = response.data.data.user;
      setUsers([newUser, ...users]);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        is_active: true,
        role_id: '',
      });
      setShowCreateDialog(false);
      
      addToast({
        title: "User Created",
        description: "User has been created successfully.",
        variant: "success"
      });
    } catch (error: any) {
      console.error('Failed to create user:', error);
      addToast({
        title: "Creation Failed",
        description: error.response?.data?.message || "Failed to create user. Please try again.",
        variant: "destructive"
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userToEdit) return;

    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim()) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (formData.password && formData.password !== formData.password_confirmation) {
      addToast({
        title: "Validation Error",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    setUpdateLoading(true);

    try {
      const updateData: UpdateUserRequest = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        is_active: formData.is_active,
        role_id: formData.role_id || undefined,
      };

      if (formData.password) {
        updateData.password = formData.password;
      }

      const response = await usersAPI.update(userToEdit.id, updateData);
      
      const updatedUser = response.data.data.user;
      setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));
      setShowEditDialog(false);
      setUserToEdit(null);
      
      addToast({
        title: "User Updated",
        description: "User has been updated successfully.",
        variant: "success"
      });
    } catch (error: any) {
      console.error('Failed to update user:', error);
      addToast({
        title: "Update Failed",
        description: error.response?.data?.message || "Failed to update user. Please try again.",
        variant: "destructive"
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleteLoading(true);
    try {
      await usersAPI.delete(userToDelete.id);
      setUsers(users.filter(u => u.id !== userToDelete.id));
      setShowDeleteDialog(false);
      setUserToDelete(null);
      
      addToast({
        title: "User Deleted",
        description: "User has been deleted successfully.",
        variant: "success"
      });
    } catch (error) {
      console.error('Failed to delete user:', error);
      addToast({
        title: "Delete Failed",
        description: "Failed to delete user. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEditDialog = (user: User) => {
    setUserToEdit(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      password: '',
      password_confirmation: '',
      is_active: user.is_active,
      role_id: user.role_id || '',
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (user: User) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const totalPages = Math.ceil(users.length / itemsPerPage);
  const paginatedUsers = users.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const hasPermission = useAuthStore((state) => state.hasPermission);

  if (!hasPermission('users.manage')) {
    return (
      <Layout title="Access Denied" description="You don't have permission to access this page">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <UsersIcon className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">Access Denied</h3>
            <p className="text-muted-foreground">
              You don't have permission to manage users.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      title="Users" 
      description="Manage system users"
    >
      <TooltipProvider>
        <div className="mobile-padding py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="flex h-10 min-w-40 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger 
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              onClick={() => {
                setFormData({
                  first_name: '',
                  last_name: '',
                  email: '',
                  password: '',
                  password_confirmation: '',
                  is_active: true,
                  role_id: '',
                });
                setShowCreateDialog(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Create User
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-hidden p-0">
              <form onSubmit={handleCreateUser} className="flex flex-col max-h-[85vh]">
                <div className="px-6 pt-6 pb-4 flex-shrink-0">
                  <DialogHeader className="space-y-3">
                    <DialogTitle>Create New User</DialogTitle>
                    <DialogDescription>
                      Add a new user to the system
                    </DialogDescription>
                  </DialogHeader>
                </div>
                
                <div className="flex-1 overflow-y-auto px-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                    {/* Left side - User fields */}
                    <div className="space-y-4">
                      <div className="space-y-3">
                        <Label htmlFor="first_name" className="text-sm font-medium">First Name</Label>
                        <Input
                          id="first_name"
                          placeholder="First name"
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="last_name" className="text-sm font-medium">Last Name</Label>
                        <Input
                          id="last_name"
                          placeholder="Last name"
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="user@example.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder="Password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="password_confirmation" className="text-sm font-medium">Confirm Password</Label>
                        <Input
                          id="password_confirmation"
                          type="password"
                          placeholder="Confirm password"
                          value={formData.password_confirmation}
                          onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    {/* Right side - Role and Active status */}
                    <div className="space-y-5 md:border-l md:pl-6">
                      <div>
                        <Label className="text-sm font-semibold">Settings</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Role and access control
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <Label htmlFor="role" className="text-sm font-medium">Role</Label>
                        <select
                          id="role"
                          value={formData.role_id}
                          onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="">No Role</option>
                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-start justify-between gap-4 py-2">
                        <div className="space-y-1 flex-1">
                          <Label htmlFor="is_active" className="text-sm font-medium cursor-pointer">Active</Label>
                          <div className="text-sm text-muted-foreground leading-relaxed">
                            User can login
                          </div>
                        </div>
                        <Switch
                          id="is_active"
                          checked={formData.is_active}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                          className="mt-1 flex-shrink-0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 pb-6 pt-4 border-t flex-shrink-0">
                  <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createLoading}>
                      {createLoading ? 'Creating...' : 'Create User'}
                    </Button>
                  </DialogFooter>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-hidden p-0">
            <form onSubmit={handleUpdateUser} className="flex flex-col max-h-[85vh]">
              <div className="px-6 pt-6 pb-4 flex-shrink-0">
                <DialogHeader className="space-y-3">
                  <DialogTitle>Edit User</DialogTitle>
                  <DialogDescription>
                    Update user information
                  </DialogDescription>
                </DialogHeader>
              </div>
              
              <div className="flex-1 overflow-y-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                  {/* Left side - User fields */}
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <Label htmlFor="edit_first_name" className="text-sm font-medium">First Name</Label>
                      <Input
                        id="edit_first_name"
                        placeholder="First name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="edit_last_name" className="text-sm font-medium">Last Name</Label>
                      <Input
                        id="edit_last_name"
                        placeholder="Last name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="edit_email" className="text-sm font-medium">Email</Label>
                      <Input
                        id="edit_email"
                        type="email"
                        placeholder="user@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="edit_password" className="text-sm font-medium">New Password (optional)</Label>
                      <Input
                        id="edit_password"
                        type="password"
                        placeholder="Leave blank to keep current"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      />
                    </div>
                    {formData.password && (
                      <div className="space-y-3">
                        <Label htmlFor="edit_password_confirmation" className="text-sm font-medium">Confirm New Password</Label>
                        <Input
                          id="edit_password_confirmation"
                          type="password"
                          placeholder="Confirm new password"
                          value={formData.password_confirmation}
                          onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                        />
                      </div>
                    )}
                  </div>

                  {/* Right side - Role and Active status */}
                  <div className="space-y-5 md:border-l md:pl-6">
                    <div>
                      <Label className="text-sm font-semibold">Settings</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Role and access control
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="edit_role" className="text-sm font-medium">Role</Label>
                      <select
                        id="edit_role"
                        value={formData.role_id}
                        onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">No Role</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-start justify-between gap-4 py-2">
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="edit_is_active" className="text-sm font-medium cursor-pointer">Active</Label>
                        <div className="text-sm text-muted-foreground leading-relaxed">
                          User can login
                        </div>
                      </div>
                      <Switch
                        id="edit_is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                        className="mt-1 flex-shrink-0"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="px-6 pb-6 pt-4 border-t flex-shrink-0">
                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateLoading}>
                    {updateLoading ? 'Updating...' : 'Update User'}
                  </Button>
                </DialogFooter>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="sm:max-w-[450px] max-h-[85vh] overflow-hidden p-0">
            <div className="flex flex-col max-h-[85vh]">
              <div className="px-6 pt-6 pb-6 flex-shrink-0">
                <DialogHeader className="space-y-4">
                  <DialogTitle>Delete User</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete this user? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
              </div>
              
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                {userToDelete && (
                  <div className="bg-muted p-5 rounded-lg space-y-3">
                    <h4 className="font-medium text-base">{userToDelete.first_name} {userToDelete.last_name}</h4>
                    <p className="text-sm text-muted-foreground">{userToDelete.email}</p>
                  </div>
                )}
              </div>
              
              <div className="px-6 pb-6 pt-5 border-t flex-shrink-0">
                <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleteLoading} className="w-full sm:w-auto">
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleDeleteUser} disabled={deleteLoading} className="w-full sm:w-auto">
                    {deleteLoading ? 'Deleting...' : 'Delete User'}
                  </Button>
                </DialogFooter>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-sm text-muted-foreground">Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <UsersIcon className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium mb-2">No users found</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first user to get started
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create User
                </Button>
              </div>
            ) : (
              <>
                <div className="hidden md:block rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            {user.first_name} {user.last_name}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {user.role ? (
                              <Badge variant="outline">{user.role.name}</Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">No role</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.is_active ? 'default' : 'secondary'}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(user.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditDialog(user)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Edit User
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openDeleteDialog(user)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Delete User
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="md:hidden divide-y divide-border">
                  {paginatedUsers.map((user) => (
                    <div key={user.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base truncate">
                            {user.first_name} {user.last_name}
                          </h3>
                          <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-9 w-9 p-0 touch-target flex-shrink-0"
                            >
                              <MoreHorizontal className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(user)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit User
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => openDeleteDialog(user)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        {user.role && (
                          <Badge variant="outline">{user.role.name}</Badge>
                        )}
                        <Badge variant={user.is_active ? 'default' : 'secondary'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>

                      <div className="text-xs text-muted-foreground">
                        Created {formatDate(user.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, users.length)} of {users.length} entries
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="touch-target"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="hidden sm:inline ml-1">Previous</span>
                      </Button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className="w-9 h-9 p-0 touch-target"
                          >
                            {page}
                          </Button>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="touch-target"
                      >
                        <span className="hidden sm:inline mr-1">Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        </div>
      </TooltipProvider>
    </Layout>
  );
};

export default Users;
